import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import Button from 'components/base/Button';
import { notify } from 'messages';

import { showDialog } from 'dialogs';
import { useAppDispatch } from 'redux/store';
import { deleteCustomerNodeDefinition, type DeleteConflictPayload } from '../../../../redux/slices/customer_node_definitionsSlice';

export interface DeletableNodeDefinition {
  zoneName: string;
  name: string;
}

const conflictKey = (node: DeletableNodeDefinition) => `${node.zoneName}::${node.name}`;

const renderConflictSummary = (conflict: DeleteConflictPayload): string | null => {
  const refs = conflict.references;
  if (!refs) return null;
  const parts: string[] = [];
  if (refs.flows.length) parts.push(`${refs.flows.length} flow(s)`);
  if (refs.triggers.length) parts.push(`${refs.triggers.length} trigger(s)`);
  if (refs.events.length) parts.push(`${refs.events.length} event(s)`);
  if (refs.integration_nodes.length) parts.push(`${refs.integration_nodes.length} integration node(s)`);
  if (refs.active_instances.total) parts.push(`${refs.active_instances.total} active instance(s)`);
  return parts.join(', ');
};

interface Props {
  nodes: readonly DeletableNodeDefinition[];
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * Conflict-aware delete for customer node definitions. Attempts each delete;
 * definitions blocked by references (flows / triggers / events / integration
 * nodes / active instances) surface their conflict summary and stay in the
 * dialog so the user can clear the references and retry. Closes (and calls
 * `onDeleted`) once nothing remains blocked.
 */
const DeleteNodeDefinitionsDialog: React.FC<Props> = ({ nodes, onClose, onDeleted }) => {
  const dispatch = useAppDispatch();
  const [targetNodes, setTargetNodes] = useState<readonly DeletableNodeDefinition[]>(nodes);
  const [conflicts, setConflicts] = useState<Record<string, DeleteConflictPayload>>({});

  const hasConflicts = Object.keys(conflicts).length > 0;

  const handleConfirm = async () => {
    const newConflicts: Record<string, DeleteConflictPayload> = {};
    const stillPending: DeletableNodeDefinition[] = [];

    for (const node of targetNodes) {
      try {
        await (dispatch(deleteCustomerNodeDefinition({ zone_name: node.zoneName, node_definition_name: node.name })) as any).unwrap();
        notify.success(`Deleted: ${node.name} (${node.zoneName})`);
      } catch (err: any) {
        // Rejected thunks return the rejected payload object here.
        if (err && typeof err === 'object' && err.references) {
          newConflicts[conflictKey(node)] = err as DeleteConflictPayload;
          stillPending.push(node);
        } else {
          const message = typeof err === 'string' ? err : err?.error || 'Unknown error';
          notify.error(`Failed deleting ${node.name}: ${message}`);
        }
      }
    }

    if (Object.keys(newConflicts).length > 0) {
      // Keep the dialog open to surface the blocking references.
      setConflicts(newConflicts);
      setTargetNodes(stillPending);
      return;
    }

    onDeleted();
    onClose();
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{hasConflicts ? 'Cannot delete' : 'Confirm deletion'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {hasConflicts ? (
          <>
            <p>The following definitions are referenced elsewhere and cannot be deleted. Remove the references first, then try again.</p>
            <ul>
              {targetNodes.map((node) => {
                const conflict = conflicts[conflictKey(node)];
                if (!conflict) return null;
                return (
                  <li key={conflictKey(node)}>
                    <strong>
                      {node.name}
                      {node.zoneName ? ` (${node.zoneName})` : ''}
                    </strong>
                    : {renderConflictSummary(conflict)}
                  </li>
                );
              })}
            </ul>
          </>
        ) : targetNodes.length === 1 ? (
          <p>Are you sure you want to delete this node definition?</p>
        ) : (
          <p>Are you sure you want to delete {targetNodes.length} node definitions?</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {hasConflicts ? 'Close' : 'Cancel'}
        </Button>
        {!hasConflicts && (
          <Button variant="danger" onClick={handleConfirm}>
            Confirm Delete
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Open the conflict-aware delete dialog for the given node definitions. `onDeleted`
 * runs once every selected definition has been deleted (nothing left blocked).
 */
export const showDeleteNodeDefinitions = (nodes: readonly DeletableNodeDefinition[], onDeleted: () => void): void => {
  if (nodes.length === 0) return;
  showDialog((close) => <DeleteNodeDefinitionsDialog nodes={nodes} onClose={close} onDeleted={onDeleted} />);
};
